// ── ParentHero ✦ — initial-circle avatar + tier + growth summary ─
//
// Constitutional rules (LOCKED):
//   • Initial-circle avatar ONLY — no prominent profile photo
//     (PRD §5.1 + Craig answer 5).
//   • Tier label + Level N rendered as plain strings, never a
//     comparison ladder.
//   • Summary line is growth-framed ("Here's what {firstName} has
//     been up to ✦") — no comparison, no percentile, no score.
//   • Stats are absolute counts only.
import React from 'react';
import {
  BG, BG_SOFT, TEXT, TEXT_MUTED, TEXT_SOFT, BLUE, VIOLET, FONT_STACK,
  TIER_COLOUR, TIER_ICON,
} from './palette';
import { Sparkle } from '@phosphor-icons/react';
import { SinceLastVisitPill } from './SinceLastVisitPill';

export const ParentHero = ({ crew, since }) => {
  const tierId = crew.tier?.id || 'rising';
  const accent = TIER_COLOUR[tierId] || BLUE;
  const TierIcon = TIER_ICON[tierId] || Sparkle;
  const firstName = crew.first_name || 'Crew member';
  const initial = (crew.initial || firstName.slice(0, 1) || '✦').toUpperCase();
  const summary = crew.summary || {};

  const stats = [
    { id: 'events',     label: 'Events done',   value: summary.events_completed || 0, accent },
    { id: 'references', label: 'References',    value: summary.references_earned || 0, accent: BLUE },
    { id: 'repeats',    label: 'Welcomed back', value: summary.repeat_requests   || 0, accent: VIOLET },
  ];

  return (
    <section
      data-testid="parent-dashboard-hero"
      style={{
        padding: '28px 24px', borderRadius: 24, marginBottom: 18,
        background: `linear-gradient(160deg, ${BG_SOFT}, ${BG})`,
        border: `1px solid ${accent}33`,
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', top: -40, right: -40, width: 160, height: 160,
        borderRadius: '50%', background: accent, opacity: 0.10, filter: 'blur(40px)',
        pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          data-testid="parent-dashboard-initial"
          style={{
            width: 58, height: 58, borderRadius: '50%',
            background: `linear-gradient(135deg, ${accent}3D, ${VIOLET}28)`,
            border: `1px solid ${accent}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 22, color: TEXT, flexShrink: 0,
            letterSpacing: '-0.02em',
          }}
        >
          {initial}
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{
            margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: accent, opacity: 0.9,
          }}>
            EventDrop Crew ✦
          </p>
          <p
            data-testid="parent-dashboard-firstname"
            style={{
              margin: '4px 0 0', fontFamily: FONT_STACK, fontWeight: 700,
              fontSize: 26, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1.1,
            }}
          >
            {firstName}
          </p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
            padding: '4px 11px', borderRadius: 999,
            background: `${accent}1A`, border: `1px solid ${accent}45`,
          }}>
            <TierIcon size={11} weight="fill" color={accent} />
            <span
              data-testid="parent-dashboard-tier"
              style={{ fontWeight: 700, fontSize: 12, color: TEXT, letterSpacing: '0.01em' }}
            >
              {crew.tier?.label || 'Rising Crew'} · Level {crew.crew_level || 1}
            </span>
          </div>
        </div>
      </div>

      <SinceLastVisitPill since={since} />

      <p
        data-testid="parent-dashboard-summary"
        style={{
          margin: '20px 0 0', color: TEXT_SOFT, fontSize: 14, lineHeight: 1.6,
        }}
      >
        Here's what {firstName} has been up to ✦
      </p>
      <div
        style={{
          marginTop: 14, display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(3, 1fr)',
        }}
      >
        {stats.map((c) => (
          <div
            key={c.id}
            data-testid={`parent-dashboard-stat-${c.id}`}
            style={{
              padding: '12px 8px', borderRadius: 14, textAlign: 'center',
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${c.accent}26`,
            }}
          >
            <p style={{
              margin: 0, fontWeight: 700, fontSize: 24, color: c.accent,
              letterSpacing: '-0.02em',
            }}>
              {c.value}
            </p>
            <p style={{
              margin: '2px 0 0', fontSize: 10, color: TEXT_MUTED,
              fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>
              {c.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};
